import {useState, FC} from 'react';
import {useTranslation} from 'react-i18next';
import {List} from '@tickchak/react-share/app';
import Add from '../../../../../../assets/icons/add';
import Ex from '../../../../../../assets/icons/ex';
import useShow, {TShow} from '../../../../../../hooks/useShow';
import useTeamMembers from '../../../../../../hooks/useTeamMembers';
import {updateReportContacts} from '../../../../../../redux/account/accountActions';
import TeamMemberPop from '../../../../../team/teamMemberPop/TeamMemberPop';
import * as css from './AddContactPop.css';

interface ContactMember {
    uid: number;
    globalUserId: number;
    nameFirst?: string;
    nameLast?: string;
    picture?: string;
    status?: string;
    sendInvoice?: number;
}

interface AddContactPopProps {
    show: TShow;
    contactMembers: ContactMember[];
}

const AddContactPop: FC<AddContactPopProps> = ({show, contactMembers}) => {
    const {t} = useTranslation('package');
    const {accountMembers} = useTeamMembers() as {accountMembers: ContactMember[]};
    const [search, setSearch] = useState<string>('');
    const [selectedIds, setSelectedIds] = useState<number[]>(() => contactMembers.map(m => m.globalUserId));
    const newContactShow = useShow();

    const gatedShow = {
        ...show,
        close: () => {
            if(newContactShow.isOpen) return Promise.resolve();
            return show.close();
        }
    };

    const verifiedMembers = accountMembers.filter(m => m.status !== 'unauthenticated');
    const filteredMembers = search
        ? verifiedMembers.filter(m => `${m.nameFirst} ${m.nameLast}`.toLowerCase().includes(search.toLowerCase()))
        : verifiedMembers;

    const getInitials = (member: ContactMember): string =>
        (member.nameFirst?.[0] || '') + (member.nameLast?.[0] || '');

    const handleItemClick = (item: {id: string | number}) => {
        const id = Number(item.id);
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleConfirm = async () => {
        await updateReportContacts(selectedIds.map(globalUserId => ({globalUserId})));
        show.close();
    };

    if(!show.isOpen) return null;

    return (
        <>
            <css.modalOverlay className={show.showClass} onClick={e => e.target === e.currentTarget && gatedShow.close()}>
                <List
                    show={gatedShow}
                    data-cy="addContactPop"
                    position={{width: '380px', height: '500px'}}
                    onItemClick={handleItemClick}
                    ariaLabel={t('addReportContact')}
                >
                    <List.Header
                        fixed
                        title={t('addReportContact')}
                        action={{
                            variant:'neutral',
                            text: t('newContact'),
                            icon: <Add width={14} height={14} />,
                            onClick: newContactShow.open
                        }}
                    />
                    <List.Search placeholder={t('search')} onChange={setSearch} autoFocus />
                    <List.Items>
                        <css.scrollWrap>
                            {filteredMembers.map(member => (
                                <List.Item.Checkbox
                                    key={member.globalUserId}
                                    id={member.globalUserId}
                                    text={`${member.nameFirst} ${member.nameLast}`}
                                    avatar={{src: member.picture, initials: getInitials(member), size: 'lg'}}
                                    selected={selectedIds.includes(member.globalUserId)}
                                />
                            ))}
                        </css.scrollWrap>
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('cancel')} variant="primary" size="category" iconStart={<Ex color="currentColor" width={10} height={10} />} />
                        <List.Footer.Button text={t('confirm')} variant="brand" size="category" onClick={handleConfirm} />
                    </List.Footer>
                </List>
            </css.modalOverlay>
            {newContactShow.isOpen && (
                <div onMouseUp={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                    <TeamMemberPop close={newContactShow.close} showClass={newContactShow.showClass} forcePackageMode defaultRole="recipient" />
                </div>
            )}
        </>
    );
};

export default AddContactPop;
